<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { getActiveMomentDateFormat, getShelterNow } from '@Constants';
import { getDefaultDateFormat } from '@Helpers';

const props = withDefaults(
  defineProps<{
    error?: string;
    label?: string;
    dateModel?: boolean;
  }>(),
  {
    label: 'animals.birthdayField.resultLabel',
  }
);

const { t } = useI18n();

const model = defineModel<Date | string>();

const dateFields = ref({
  year: {
    label: t('common.year', 2),
    model: 0,
    min: undefined,
    max: undefined,
  },
  month: {
    label: t('common.month', 2),
    model: 0,
    min: 0,
    max: undefined,
  },
  days: {
    label: t('common.day', 2),
    model: 0,
    min: 0,
    max: undefined,
  },
});

// NOTE :: Was a format→re-parse round trip through 'MM/DD/YY' just to truncate the time.
const currentDate = computed(() => moment(getShelterNow()).startOf('day').toDate());
const composedDate = computed(() => {
  const { year, month, days } = dateFields.value;

  const newDate = moment(currentDate.value)
    .subtract({
      years: year.model,
      months: month.model,
      days: days.model,
    })
    .toDate();

  // NOTE :: The composed value rides out as ISO, not 'MM/DD/YY'. It is both this
  // component's internal round-trip value AND (when `dateModel` is false) the string it
  // emits as the model — a two-digit-year US string is ambiguous to every parser downstream.
  return moment(newDate).format('YYYY-MM-DD');
});
const composedDateDisplay = computed(() => getDefaultDateFormat(composedDate.value));
const noRecord = computed(() => {
  const { year, month, days } = dateFields.value;

  return !year.model && !month.model && !days.model;
});

watch(composedDate, (newDate) => updateModel(newDate));

onMounted(() => setTimeout(() => setInitialModel(), 50));

function setInitialModel() {
  if (!model.value) return;

  // NOTE :: Active shelter format FIRST — list order decides an ambiguous string. Without it
  // a `d/m/Y` value from the API failed silently and the field rendered empty. The animal
  // profile sends the birthday with a time, hence the `h:mm A` format.
  const SHELTER_DATE_FORMAT = getActiveMomentDateFormat();
  const PARSE_FORMATS = [
    SHELTER_DATE_FORMAT,
    `${SHELTER_DATE_FORMAT} h:mm A`,
    'YYYY-MM-DD',
    'MM-DD-YYYY',
    'MM/DD/YYYY',
    'MM/DD/YY',
  ];

  const date = moment(model.value, PARSE_FORMATS, true).startOf('day');

  if (!date.isValid()) return;

  const diffYears = moment(currentDate.value).diff(date, 'years');
  const diffMonths = moment(currentDate.value).subtract(diffYears, 'years').diff(date, 'months');
  const diffDays = moment(currentDate.value)
    .subtract(diffYears, 'years')
    .subtract(diffMonths, 'months')
    .diff(date, 'days');

  dateFields.value.year.model = diffYears;
  dateFields.value.month.model = diffMonths;
  dateFields.value.days.model = diffDays;
}

function updateModel(newDate: string) {
  if (noRecord.value) {
    model.value = undefined;
    return;
  }

  if (!props.dateModel) model.value = newDate;

  const date = moment(newDate, 'YYYY-MM-DD', true);
  model.value = date.toDate();
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="flex flex-wrap gap-2">
      <fieldset
        v-for="field in dateFields"
        class="flex flex-col"
        :key="field.label"
      >
        <AFieldLabel
          :error
          :hasWhiteBackground="false"
          :label="field.label"
        />

        <InputNumber
          v-model="field.model"
          :max="field.max"
          :min="field.min"
          :pt:input:root="['text-sm w-12 p-1', { 'af-field-error': !!error }]"
          @input="(event) => (field.model = Number(event.value))"
        />
      </fieldset>
    </div>

    <div class="flex gap-1 text-xs">
      <span class="font-medium"> {{ $t(label) }}: </span>

      <span>
        {{ !noRecord ? composedDateDisplay : '-' }}
      </span>
    </div>
  </div>
</template>
